import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Volume2, VolumeX, ArrowLeft, Sparkles, Send, Keyboard, Inbox } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ReceivedMessages } from "@/components/communication/ReceivedMessages";

const CHAT_URL = `${process.env.REACT_APP_BACKEND_URL}/functions/v1/grafton-chat`;
const TTS_URL = `${process.env.REACT_APP_BACKEND_URL}/functions/v1/elevenlabs-tts`;

type VoiceState = "idle" | "listening" | "thinking" | "speaking";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches ||
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

// ── Orb Animation Component ──
const GraftonOrb = ({ state }: { state: VoiceState }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const size = 280;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    let t = 0;

    const getConfig = () => {
      switch (state) {
        case "listening": return { speed: 0.04, amplitude: 18, rings: 4, glow: "210, 170, 100", pulseSpeed: 0.08 };
        case "thinking": return { speed: 0.06, amplitude: 12, rings: 5, glow: "180, 140, 80", pulseSpeed: 0.12 };
        case "speaking": return { speed: 0.03, amplitude: 22, rings: 4, glow: "220, 180, 120", pulseSpeed: 0.05 };
        default: return { speed: 0.015, amplitude: 8, rings: 3, glow: "160, 130, 70", pulseSpeed: 0.02 };
      }
    };

    const draw = () => {
      const cfg = getConfig();
      t += cfg.speed;
      ctx.clearRect(0, 0, size, size);

      // Outer glow
      const glowRadius = 80 + Math.sin(t * cfg.pulseSpeed * 10) * 10;
      const grad = ctx.createRadialGradient(cx, cy, 20, cx, cy, glowRadius + 40);
      grad.addColorStop(0, `rgba(${cfg.glow}, 0.15)`);
      grad.addColorStop(0.5, `rgba(${cfg.glow}, 0.05)`);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);

      // Rings
      for (let r = 0; r < cfg.rings; r++) {
        const baseR = 35 + r * 15;
        const alpha = 0.3 - r * 0.06;
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2; a += 0.02) {
          const wave = Math.sin(a * 3 + t + r * 1.2) * cfg.amplitude * (1 - r * 0.15);
          const radius = baseR + wave;
          const x = cx + Math.cos(a) * radius;
          const y = cy + Math.sin(a) * radius;
          a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${cfg.glow}, ${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Core
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 30);
      coreGrad.addColorStop(0, `rgba(${cfg.glow}, 0.4)`);
      coreGrad.addColorStop(0.6, `rgba(${cfg.glow}, 0.15)`);
      coreGrad.addColorStop(1, `rgba(${cfg.glow}, 0)`);
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, 30, 0, Math.PI * 2);
      ctx.fill();

      // Center letter
      ctx.fillStyle = `rgba(${cfg.glow}, 0.9)`;
      ctx.font = "bold 24px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("G", cx, cy);

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [state]);

  return <canvas ref={canvasRef} className="relative z-10" />;
};

// ── Audio Bars ──
const AudioBars = ({ active, count = 7 }: { active: boolean; count?: number }) => (
  <div className="flex items-end gap-1 h-12 justify-center">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        className="w-1 rounded-full"
        style={{ background: "linear-gradient(to top, hsl(var(--primary)), hsl(var(--primary) / 0.3))" }}
        animate={
          active
            ? {
                height: [8, 20 + Math.random() * 28, 12, 30 + Math.random() * 18, 8],
              }
            : { height: 4 }
        }
        transition={
          active
            ? {
                duration: 0.6 + Math.random() * 0.4,
                repeat: Infinity,
                repeatType: "mirror",
                delay: i * 0.08,
              }
            : { duration: 0.3 }
        }
      />
    ))}
  </div>
);

// ── Split markdown text into display blocks ──
const splitIntoBlocks = (text: string): string[] => {
  // Remove ACTION lines
  const cleaned = text.replace(/^ACTION:.*$/gm, "").trim();
  // Split on double newlines or headings
  const blocks = cleaned
    .split(/\n{2,}|(?=^## )/m)
    .map((b) => b.trim())
    .filter((b) => b.length > 0);
  return blocks.length > 0 ? blocks : [cleaned];
};

// ── Auto-rotating assistant transcript ──
const AssistantTranscript = ({ text }: { text: string }) => {
  const blocks = splitIntoBlocks(text);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [text]);

  useEffect(() => {
    if (blocks.length <= 1) return;
    const timer = setInterval(() => {
      setIdx((prev) => (prev + 1) % blocks.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [blocks.length]);

  return (
    <div className="relative min-h-[80px] flex flex-col items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          className="prose prose-sm prose-invert prose-headings:text-primary prose-strong:text-primary max-w-none text-center"
        >
          <ReactMarkdown>{blocks[idx]}</ReactMarkdown>
        </motion.div>
      </AnimatePresence>

      {/* Dots indicator */}
      {blocks.length > 1 && (
        <div className="flex gap-1.5 mt-3">
          {blocks.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx
                  ? "w-4 bg-primary/70"
                  : "w-1.5 bg-primary/20 hover:bg-primary/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Transcript Display ──
const TranscriptDisplay = ({ text, role }: { text: string; role: "user" | "assistant" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`max-w-lg mx-auto text-center ${
      role === "user" ? "text-muted-foreground text-sm italic" : "text-foreground"
    }`}
  >
    {role === "assistant" ? (
      <AssistantTranscript text={text} />
    ) : (
      <p>"{text}"</p>
    )}
  </motion.div>
);

// ── Main Component ──
export default function CommunicationDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [isMuted, setIsMuted] = useState(() => isTouchDevice());
  const [started, setStarted] = useState(false);
  const [history, setHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [activeTab, setActiveTab] = useState<"voice" | "messages">("messages");

  const recognitionRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  // ── Speech Recognition Setup ──
  const startListening = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome.");
      setShowTextInput(true);
      return;
    }

    // Request mic permission first
    navigator.mediaDevices?.getUserMedia({ audio: true }).then((stream) => {
      // Stop the stream immediately — we only needed to trigger the permission prompt
      stream.getTracks().forEach(t => t.stop());

      const recognition = new SpeechRecognition();
      recognition.lang = "fr-FR";
      recognition.continuous = false;
      recognition.interimResults = true;
      recognitionRef.current = recognition;

      let finalTranscript = "";

      recognition.onresult = (event: any) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setTranscript(finalTranscript || interim);
      };

      recognition.onend = () => {
        if (finalTranscript.trim()) {
          sendToGrafton(finalTranscript.trim());
        } else {
          setVoiceState("idle");
        }
      };

      recognition.onerror = (e: any) => {
        console.error("Speech recognition error:", e.error);
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          toast.error("Accès au micro refusé. Utilisez la saisie texte ci-dessous.");
          setShowTextInput(true);
        } else if (e.error === "aborted") {
          toast.error("Reconnaissance vocale interrompue. Réessayez.");
        } else if (e.error === "no-speech") {
          toast.info("Aucune parole détectée. Réessayez ou utilisez la saisie texte.");
          setShowTextInput(true);
        } else {
          toast.error(`Erreur micro : ${e.error}. Utilisez la saisie texte.`);
          setShowTextInput(true);
        }
        setVoiceState("idle");
      };

      setVoiceState("listening");
      setTranscript("");
      setResponse("");
      recognition.start();
    }).catch(() => {
      toast.error("Micro non disponible. Utilisez la saisie texte.");
      setShowTextInput(true);
      setVoiceState("idle");
    });
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  // ── Send to Grafton IA ──
  const sendToGrafton = async (text: string) => {
    setVoiceState("thinking");
    const newHistory = [...history, { role: "user", content: text }];

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${""}`,
        },
        body: JSON.stringify({
          messages: newHistory,
          context: {},
          stream: false,
        }),
      });

      if (resp.status === 429) {
        toast.error("Trop de requêtes, réessayez dans un instant.");
        setVoiceState("idle");
        return;
      }
      if (resp.status === 402) {
        toast.error("Crédits IA épuisés.");
        setVoiceState("idle");
        return;
      }
      if (!resp.ok) throw new Error("Erreur");

      const data = await resp.json();
      const reply = data.reply || "Désolé, je n'ai pas compris.";
      
      setResponse(reply);
      setHistory([...newHistory, { role: "assistant", content: reply }]);

      // Speak the response
      if (!isMuted) {
        speakResponse(reply);
      } else {
        setVoiceState("idle");
      }
    } catch {
      toast.error("Erreur de communication avec Grafton IA.");
      setVoiceState("idle");
    }
  };

  const stopCurrentSpeech = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  };

  // ── Fallback: Browser native TTS ──
  const speakWithBrowserTTS = (text: string) => {
    if (!("speechSynthesis" in window)) {
      toast.error("La synthèse vocale n'est pas disponible sur ce navigateur.");
      setVoiceState("idle");
      return;
    }

    const cleanText = text
      .replace(/#{1,6}\s/g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/- /g, "")
      .replace(/\n+/g, ". ")
      .replace(/\s+/g, " ")
      .trim();

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = "fr-FR";
    utterance.rate = 1.0;
    utterance.onend = () => setVoiceState("idle");
    utterance.onerror = () => {
      toast.error("Impossible de lire la réponse vocale.");
      setVoiceState("idle");
    };

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  // ── Text-to-Speech via ElevenLabs with browser fallback ──
  const speakResponse = async (text: string) => {
    stopCurrentSpeech();
    setVoiceState("speaking");

    try {
      const resp = await fetch(TTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: "",
          Authorization: `Bearer ${""}`,
        },
        body: JSON.stringify({ text }),
      });

      if (!resp.ok) {
        let errorMessage = "TTS failed";

        try {
          const errorData = await resp.json();
          errorMessage = errorData?.error ?? errorMessage;
        } catch {
          // Ignore JSON parsing errors and fall back below
        }

        throw new Error(errorMessage);
      }

      const contentType = resp.headers.get("content-type") ?? "";

      if (contentType.includes("application/json")) {
        const data = await resp.json();

        if (data?.fallback === "browser_tts") {
          speakWithBrowserTTS(text);
          return;
        }

        throw new Error(data?.error ?? "TTS failed");
      }

      const audioBlob = await resp.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setVoiceState("idle");
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
        speakWithBrowserTTS(text);
      };

      await audio.play();
    } catch (err) {
      console.warn("ElevenLabs TTS indisponible, fallback navigateur:", err);
      speakWithBrowserTTS(text);
    }
  };

  const toggleMute = () => {
    if (!isMuted) {
      stopCurrentSpeech();
      setVoiceState("idle");
    }
    setIsMuted(!isMuted);
  };

  const handleMicClick = () => {
    if (voiceState === "listening") {
      stopListening();
    } else if (voiceState === "idle") {
      startListening();
    } else if (voiceState === "speaking") {
      stopCurrentSpeech();
      setVoiceState("idle");
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stopCurrentSpeech();
    };
  }, []);

  const stateLabel: Record<VoiceState, string> = {
    idle: "Appuyez pour parler",
    listening: "Je vous écoute...",
    thinking: "Je réfléchis...",
    speaking: "Je vous réponds...",
  };

  if (authLoading) return null;

  // Splash screen
  if (!started) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50">
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(rgba(200,170,100,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(200,170,100,0.3) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
        {/* Vignette */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)",
        }} />

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 flex flex-col items-center gap-6"
        >
          <h1 className="text-4xl font-serif text-white tracking-wide">
            Grafton <span className="text-primary">IA</span>
          </h1>
          <p className="text-xs uppercase tracking-[0.3em] text-primary/60">
            L'entreprise augmentée
          </p>

          <motion.div className="my-8">
            <GraftonOrb state="idle" />
          </motion.div>

          <Button
            onClick={() => setStarted(true)}
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/60 px-8 py-3 text-sm uppercase tracking-widest rounded-full"
          >
            Appuyer pour commencer
          </Button>
          <p className="text-xs text-muted-foreground/50 mt-2">
            Activez le son pour une expérience complète
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-black">
        <DashboardSidebar />
        <main className="flex-1 relative overflow-hidden">
          {/* Subtle grid */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: "linear-gradient(rgba(200,170,100,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(200,170,100,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
          {/* Vignette */}
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)",
          }} />

          {/* Top bar */}
          <div className="relative z-20 flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              {/* Tab switcher */}
              <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab("messages")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    activeTab === "messages"
                      ? "bg-primary/20 text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Inbox className="h-3.5 w-3.5" />
                  Messages
                </button>
                <button
                  onClick={() => { setActiveTab("voice"); if (!started) setStarted(true); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    activeTab === "voice"
                      ? "bg-primary/20 text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Vocal IA
                </button>
              </div>
            </div>
            {activeTab === "voice" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="text-muted-foreground hover:text-foreground"
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {/* Messages tab */}
          {activeTab === "messages" && (
            <div className="relative z-10 p-6 max-w-3xl mx-auto" style={{ minHeight: "calc(100vh - 80px)" }}>
              <ReceivedMessages />
            </div>
          )}

          {/* Voice tab */}
          {activeTab === "voice" && (
          <div className="relative z-10 flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 80px)" }}>
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute top-4 left-1/2 -translate-x-1/2"
            >
              <h2 className="text-lg font-serif text-white/40 tracking-wide">
                Grafton <span className="text-primary/60">IA</span>
              </h2>
            </motion.div>

            {/* Orb */}
            <motion.div
              className="mb-6"
              animate={{
                scale: voiceState === "listening" ? 1.05 : voiceState === "speaking" ? 1.08 : 1,
              }}
              transition={{ duration: 0.5 }}
            >
              <GraftonOrb state={voiceState} />
            </motion.div>

            {/* Audio bars */}
            <div className="mb-6">
              <AudioBars active={voiceState === "listening" || voiceState === "speaking"} />
            </div>

            {/* State label */}
            <AnimatePresence mode="wait">
              <motion.p
                key={voiceState}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="text-sm text-primary/70 mb-6 uppercase tracking-widest"
              >
                {stateLabel[voiceState]}
              </motion.p>
            </AnimatePresence>

            {/* Transcript area */}
            <div className="w-full max-w-xl px-6 space-y-4 mb-8" style={{ minHeight: 80 }}>
              <AnimatePresence>
                {transcript && (
                  <TranscriptDisplay text={transcript} role="user" />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {response && voiceState !== "listening" && (
                  <TranscriptDisplay text={response} role="assistant" />
                )}
              </AnimatePresence>
            </div>

            {/* Mic button */}
            <motion.button
              onClick={handleMicClick}
              disabled={voiceState === "thinking"}
              className={`relative h-16 w-16 rounded-full flex items-center justify-center transition-all ${
                voiceState === "listening"
                  ? "bg-destructive/20 border-2 border-destructive/60 text-destructive"
                  : voiceState === "thinking"
                  ? "bg-primary/10 border-2 border-primary/30 text-primary/50 cursor-wait"
                  : "bg-primary/15 border-2 border-primary/40 text-primary hover:bg-primary/25 hover:border-primary/60"
              }`}
              whileTap={voiceState !== "thinking" ? { scale: 0.95 } : {}}
            >
              {voiceState === "thinking" ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="h-6 w-6" />
                </motion.div>
              ) : voiceState === "listening" ? (
                <MicOff className="h-6 w-6" />
              ) : (
                <Mic className="h-6 w-6" />
              )}

              {/* Pulse ring when listening */}
              {voiceState === "listening" && (
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-destructive/30"
                  animate={{ scale: [1, 1.4], opacity: [0.6, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
            </motion.button>

            {/* Text input fallback */}
            {showTextInput && voiceState === "idle" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md px-6 mt-4"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && textInput.trim()) {
                        setTranscript(textInput.trim());
                        sendToGrafton(textInput.trim());
                        setTextInput("");
                      }
                    }}
                    placeholder="Tapez votre question..."
                    className="flex-1 bg-secondary/30 border border-border/30 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/40"
                  />
                  <button
                    onClick={() => {
                      if (textInput.trim()) {
                        setTranscript(textInput.trim());
                        sendToGrafton(textInput.trim());
                        setTextInput("");
                      }
                    }}
                    disabled={!textInput.trim()}
                    className="h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center text-primary hover:bg-primary/25 transition-colors disabled:opacity-30"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Toggle text input */}
            {!showTextInput && voiceState === "idle" && (
              <button
                onClick={() => setShowTextInput(true)}
                className="mt-4 text-xs text-muted-foreground/40 hover:text-muted-foreground/70 flex items-center gap-1.5 transition-colors"
              >
                <Keyboard className="h-3.5 w-3.5" />
                Passer en mode texte
              </button>
            )}

            {/* Hints */}
            <p className="text-xs text-muted-foreground/30 mt-6 max-w-sm text-center">
              {voiceState === "idle" && history.length === 0 && !showTextInput
                ? "Posez une question à votre conseiller IA. Par exemple : « Comment améliorer mon chiffre d'affaires ? »"
                : voiceState === "speaking"
                ? "Cliquez sur le micro pour interrompre"
                : ""}
            </p>
          </div>
          )}
        </main>
      </div>
    </SidebarProvider>
  );
}
